import clsx from "clsx";
import { useMemo } from "react";
import { ACCOUNT_LIST } from "../labeling.constants";
import { LabelItem } from "./LabelItem";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { NormalizedAccount } from "../state";
import {
    useHotkey,
    getShortcutDisplay,
} from "@/ui/organisms/FloatingMenu/useHotkey";

export function AccountItem({
    account,
    recommended = false,
    selected = false,
    onAccountSelection = () => {},
    hotkeyNumber,
}: {
    account: NormalizedAccount;
    recommended?: boolean;
    selected?: boolean;
    onAccountSelection?: (account: NormalizedAccount) => void;
    hotkeyNumber?: number;
}) {
    const command = useMemo(() => {
        if (!hotkeyNumber) return undefined;
        return {
            label: account.name,
            shortcut: {
                key: String(hotkeyNumber),
                mac: `⌘${hotkeyNumber}`,
                windows: `Ctrl+${hotkeyNumber}`,
            },
            onClick: () => onAccountSelection(account),
        };
    }, [hotkeyNumber, account, onAccountSelection]);

    useHotkey({ command });

    const hotkeyLabel = command ? getShortcutDisplay(command.shortcut) : null;

    return (
        <li
            key={account.id}
            className={clsx(
                "card relative flex flex-col gap-2",
                recommended && "border-default mb-2 border",
                selected && "selected",
            )}
            onClick={() => onAccountSelection(account)}
        >
            <div className="flex items-start justify-between font-semibold">
                {account.name}
                <div className="flex items-center gap-2">
                    {recommended && (
                        <SparklesIcon className="text-subdued size-4" />
                    )}
                    {hotkeyLabel && (
                        <span
                            className={clsx(
                                "bg-gray-500 px-1 py-0.5",
                                "text-bg-default text-slate-800",
                                "body-micro rounded-md",
                            )}
                        >
                            {hotkeyLabel}
                        </span>
                    )}
                </div>
            </div>
            <LabelItem
                listItem={false}
                label={ACCOUNT_LIST[account.label]}
                dot
            />
        </li>
    );
}
